import { useTranslate } from '@refinedev/core';
import { Alert, Space, Typography } from 'antd';
import { pagesLabel, sourceOf, type ImportNote, type ImportedFrom } from './importModel';

/**
 * E34 · the imported draft's banner (block 5b; screen "Practice — Worksheets — Builder — Imported draft",
 * Behavior `help: the banner …, i18n: worksheets.import.draft.banner`): where the draft came from, that
 * nothing is saved until Save, and what each guard rail did to it (the translation's notes — codes localized
 * here: a link that became a section's video, scoring switched off, the agree box added, …).
 */
export const ImportNotice = ({
  importedFrom,
  notes,
}: {
  importedFrom: ImportedFrom | null;
  notes: readonly ImportNote[];
}) => {
  const t = useTranslate();
  const source = sourceOf(importedFrom);
  const noteText = (n: ImportNote): string => {
    const params: Record<string, unknown> = {
      source: n.source === 'vimeo' ? 'Vimeo' : 'YouTube',
      language: n.ref ?? '',
      count: n.count ?? 0,
    };
    const body = t(`worksheets.import.notes.${n.code}`, params);
    const pages = n.pages?.length
      ? t('worksheets.import.notes.pages', { pages: pagesLabel(n.pages), count: n.pages.length })
      : null;
    return pages ? `${pages}: ${body}` : body;
  };
  return (
    <Alert
      type="info"
      showIcon
      data-testid="worksheet-import-banner"
      message={t('worksheets.import.draft.banner', {
        source: t(source.key, source.count !== undefined ? { count: source.count } : {}),
      })}
      description={
        notes.length ? (
          <Space direction="vertical" size={2} data-testid="worksheet-import-notes">
            {notes.map((n, i) => (
              <Typography.Text key={i} data-testid={`worksheet-import-note-${n.code}`}>
                {noteText(n)}
              </Typography.Text>
            ))}
          </Space>
        ) : undefined
      }
    />
  );
};
